import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./callback_requests.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";

interface Employee {
  _id: string;
  name: String;
  phoneNumber: string;
  week: string;
  day: string;
  date: string;
  timeSlot: string;
  isResolved: Boolean;
  __v: number;
}

const CallbackRequests = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(
    null
  );
  const [showResolved, setShowResolved] = useState<boolean>(false); // Toggle between Open and Resolved
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [searchTerm, setSearchTerm] = useState<string>("");

  function formatDateToIST(dateString: string | number | Date) {
    const date = new Date(dateString);
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Intl.DateTimeFormat("en-IN").format(istDate);
  }

  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllCallBackrequests(token);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllCallBackrequests = async (token: string) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-callback-requests`,
        config
      );
      setEmployees(response.data.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleQueryResolved = async () => {
    if (!employeeToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(
        `https://api.fitoja.in/api/set-callback-request-true/${employeeToDelete.phoneNumber}`
      );
      fetchAllCallBackrequests(token);
      closeQueryResolvedModal();
    } catch (error) {
      console.error("Failed to resolve the request", error);
    }
  };

  const openQueryResolvedModal = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteModalVisible(true);
  };

  const closeQueryResolvedModal = () => {
    setDeleteModalVisible(false);
    setEmployeeToDelete(null);
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  // // Filter employees based on the `isResolved` state
  // const filteredEmployees = employees.filter(
  //   (employee) => employee.isResolved === showResolved
  // );

  // Filter employees based on the `isResolved` state and `searchTerm`
  const filteredEmployees = employees
    .filter((employee) => employee.isResolved === showResolved)
    .filter((employee) => employee.phoneNumber.includes(searchTerm));

  return (
    <div className="callback-container">
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <div className="employee-list">
            <h2>All callback requests</h2>
            <input
              className="search-box"
              type="text"
              placeholder="Search user by Phone Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="toggle-container">
              <div
                className={`toggle-option ${!showResolved ? "active" : ""}`}
                onClick={() => setShowResolved(false)}
              >
                Open Requests
              </div>
              <div
                className={`toggle-option ${showResolved ? "active" : ""}`}
                onClick={() => setShowResolved(true)}
              >
                Resolved Requests
              </div>
            </div>

            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((employee) => (
                <div key={employee._id} className="employee-card">
                  <div className="employee-details">
                    <p>
                      <strong>Name:</strong> <span>{employee.name}</span>
                    </p>
                    <p>
                      <strong>Phone Number:</strong>{" "}
                      <span>{employee.phoneNumber}</span>
                    </p>
                    <p>
                      <strong>Calling date:</strong>{" "}
                      <span>{formatDateToIST(employee.date)}</span>
                    </p>
                    <p>
                      <strong>Calling day:</strong> <span>{employee.day}</span>
                    </p>
                    <p>
                      <strong>Time slot:</strong>{" "}
                      <span>{employee.timeSlot}</span>
                    </p>
                  </div>
                  {!employee.isResolved ? (
                    <div
                      className="mark-issue-resolved"
                      onClick={() => openQueryResolvedModal(employee)}
                    >
                      <button className="mark-issue-resolved-btn">
                        Mark this issue as Resolved
                      </button>
                    </div>
                  ) : (
                    <div className="issue-resolved">
                      <button className="issue-resolved-btn">Resolved</button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-requests">
                <p>No {showResolved ? "Resolved" : "Open"} Requests found</p>
              </div>
            )}
          </div>

          {deleteModalVisible && (
            <div className="delete-modal">
              <div className="delete-modal-content">
                <h2>Query Resolved Confirmation</h2>
                <p>Are you sure you want to mark this issue as Resolved?</p>
                <span style={{ display: "flex", gap: "20px" }}>
                  <button
                    onClick={closeQueryResolvedModal}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleQueryResolved}
                    className="confirm-delete-button"
                  >
                    Yes, It's Resolved
                  </button>
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CallbackRequests;
